﻿import type { Feature, FeatureCollection, Point } from 'geojson';
import { GeoJSONSource, MapLayerMouseEvent, Map as MaplibreMap, ExpressionSpecification } from 'maplibre-gl';
import { AirportRvrInfo } from '../services/actor-messages';
import { RunwayOBB } from './runway-geometry';
import { assignRunwayEnds, formatRvrValue, getRvrColor, isRvrValueValid, RvrSegment } from '../airports/runway-rvr';

export const RVR_SOURCE_ID = 'fe-runway-rvr-source';
export const RVR_LAYER_ID = 'fe-runway-rvr';
const RVR_HOVER_LAYER_ID = 'fe-runway-rvr-hover';
const RVR_ICON_ID = 'fe-rvr-square';

const RVR_MIN_ZOOM = 12;
const RVR_ICON_HALO_COLOR = '#0b0b0b';
const RVR_ICON_HALO_WIDTH = 0.4224;
const RVR_HOVER_SCALE = 1.3;

const ICON_SIZE_STOPS: [number, number][] = [
    [RVR_MIN_ZOOM, 0.15488],
    [14, 0.22528],
    [16, 0.32384],
    [18, 0.4224]
];

const MP_CENTER_T = 0.5;
const END_A_CENTER_T = 0.12;
const END_B_CENTER_T = 0.88;

export interface RvrFeatureProperties {
    segment: RvrSegment;
    runwayId: string;
    value: number;
    label: string;
    color: string;
    rotation: number;
}

export class RunwayRvrLayer {
    private map: MaplibreMap | null = null;
    private hoveredId: number | string | undefined;

    onAdd(map: MaplibreMap, beforeLayerId: string): void {
        this.map = map;

        if (!map.hasImage(RVR_ICON_ID)) {
            map.addImage(RVR_ICON_ID, createRoundedSquareIcon(), { sdf: true });
        }

        if (!map.getSource(RVR_SOURCE_ID)) {
            map.addSource(RVR_SOURCE_ID, {
                type: 'geojson',
                data: emptyCollection(),
                generateId: true
            });
        }

        if (!map.getLayer(RVR_LAYER_ID)) {
            map.addLayer(
                {
                    id: RVR_LAYER_ID,
                    type: 'symbol',
                    source: RVR_SOURCE_ID,
                    minzoom: RVR_MIN_ZOOM,
                    layout: {
                        'icon-image': RVR_ICON_ID,
                        'icon-size': ['interpolate', ['linear'], ['zoom'], ...ICON_SIZE_STOPS.flat()] as ExpressionSpecification,
                        'icon-rotate': ['get', 'rotation'],
                        'icon-rotation-alignment': 'map',
                        'icon-allow-overlap': true,
                        'icon-ignore-placement': true
                    },
                    paint: {
                        'icon-color': ['get', 'color'],
                        'icon-halo-color': RVR_ICON_HALO_COLOR,
                        'icon-halo-width': RVR_ICON_HALO_WIDTH
                    }
                },
                beforeLayerId
            );
        }

        if (!map.getLayer(RVR_HOVER_LAYER_ID)) {
            map.addLayer(
                {
                    id: RVR_HOVER_LAYER_ID,
                    type: 'symbol',
                    source: RVR_SOURCE_ID,
                    minzoom: RVR_MIN_ZOOM,
                    filter: ['==', ['id'], -1],
                    layout: {
                        'icon-image': RVR_ICON_ID,
                        'icon-size': [
                            'interpolate',
                            ['linear'],
                            ['zoom'],
                            ...ICON_SIZE_STOPS.flatMap(([z, s]) => [z, s * RVR_HOVER_SCALE])
                        ] as ExpressionSpecification,
                        'icon-rotate': ['get', 'rotation'],
                        'icon-rotation-alignment': 'map',
                        'icon-allow-overlap': true,
                        'icon-ignore-placement': true
                    },
                    paint: {
                        'icon-color': ['get', 'color'],
                        'icon-halo-color': RVR_ICON_HALO_COLOR,
                        'icon-halo-width': RVR_ICON_HALO_WIDTH
                    }
                },
                beforeLayerId
            );
        }

        map.on('mousemove', RVR_LAYER_ID, this.handleMouseMove);
        map.on('mouseleave', RVR_LAYER_ID, this.handleMouseLeave);
    }

    onRemove(map: MaplibreMap): void {
        map.off('mousemove', RVR_LAYER_ID, this.handleMouseMove);
        map.off('mouseleave', RVR_LAYER_ID, this.handleMouseLeave);

        if (map.getLayer(RVR_LAYER_ID)) {
            map.removeLayer(RVR_LAYER_ID);
        }
        if (map.getLayer(RVR_HOVER_LAYER_ID)) {
            map.removeLayer(RVR_HOVER_LAYER_ID);
        }
        if (map.getSource(RVR_SOURCE_ID)) {
            map.removeSource(RVR_SOURCE_ID);
        }
        this.map = null;
        this.hoveredId = undefined;
    }

    update(obbs: RunwayOBB[], rvrMap: Map<string, AirportRvrInfo>): void {
        const map = this.map;
        if (!map) {
            return;
        }

        const source = map.getSource(RVR_SOURCE_ID) as GeoJSONSource | undefined;
        if (!source) {
            return;
        }

        // Index rvr info by airport identifiers for fast lookup
        const rvrByIcao = new Map<string, AirportRvrInfo>();
        const rvrByIata = new Map<string, AirportRvrInfo>();
        const rvrByFaa = new Map<string, AirportRvrInfo>();
        for (const rvr of rvrMap.values()) {
            if (rvr.icaoId) { rvrByIcao.set(rvr.icaoId, rvr); }
            if (rvr.iataId) { rvrByIata.set(rvr.iataId, rvr); }
            if (rvr.faaId) { rvrByFaa.set(rvr.faaId, rvr); }
        }

        const features: Feature<Point>[] = [];
        const processedRunways = new Set<string | number>();

        for (const obb of obbs) {
            if (obb.featureId != null && processedRunways.has(obb.featureId)) {
                continue;
            }
            if (obb.featureId != null) {
                processedRunways.add(obb.featureId);
            }

            const icaoId = obb.properties.icaoid as string | undefined;
            const iataId = obb.properties.iataid as string | undefined;
            const faaId = obb.properties.faaid as string | undefined;

            const rvrInfo =
                (icaoId ? rvrByIcao.get(icaoId) : undefined) ??
                (iataId ? rvrByIata.get(iataId) : undefined) ??
                (faaId ? rvrByFaa.get(faaId) : undefined);

            if (!rvrInfo || rvrInfo.items.length === 0) {
                continue;
            }

            const baseId = obb.properties.baseid as string | undefined;
            const recipId = obb.properties.recipid as string | undefined;
            const baseItem = baseId ? rvrInfo.items.find((i) => i.runwayId?.toUpperCase() === baseId.toUpperCase()) : undefined;
            const recipItem = recipId ? rvrInfo.items.find((i) => i.runwayId?.toUpperCase() === recipId.toUpperCase()) : undefined;

            if (!baseItem && !recipItem) {
                continue;
            }

            const midPointValue = isRvrValueValid(baseItem?.midPointVisibility)
                ? baseItem!.midPointVisibility
                : isRvrValueValid(recipItem?.midPointVisibility)
                  ? recipItem!.midPointVisibility
                  : null;
            if (isRvrValueValid(midPointValue)) {
                addPoint(features, obb, MP_CENTER_T, 'MP', baseId ?? recipId ?? '', midPointValue);
            }

            const ends = assignRunwayEnds(obb.bearingDeg, baseId ?? null, recipId ?? null);
            if (!ends) {
                continue;
            }

            // Touchdown zone RVR sits at the threshold of that runway id; rollout RVR sits at the
            // opposite (departure) end, which is the same physical spot as the reciprocal id's TD.
            // TD always wins over RO when both would land on the same physical end.
            const baseEndT = ends.baseEnd === 'endA' ? END_A_CENTER_T : END_B_CENTER_T;
            const recipEndT = ends.recipEnd === 'endA' ? END_A_CENTER_T : END_B_CENTER_T;

            if (isRvrValueValid(baseItem?.touchdownVisibility)) {
                addPoint(features, obb, baseEndT, 'TD', baseId ?? '', baseItem!.touchdownVisibility);
            } else if (isRvrValueValid(recipItem?.rolloutVisibility)) {
                addPoint(features, obb, baseEndT, 'RO', recipId ?? '', recipItem!.rolloutVisibility);
            }

            if (isRvrValueValid(recipItem?.touchdownVisibility)) {
                addPoint(features, obb, recipEndT, 'TD', recipId ?? '', recipItem!.touchdownVisibility);
            } else if (isRvrValueValid(baseItem?.rolloutVisibility)) {
                addPoint(features, obb, recipEndT, 'RO', baseId ?? '', baseItem!.rolloutVisibility);
            }
        }

        source.setData({ type: 'FeatureCollection', features });
    }

    clear(): void {
        const source = this.map?.getSource(RVR_SOURCE_ID) as GeoJSONSource | undefined;
        source?.setData(emptyCollection());
    }

    private handleMouseMove = (e: MapLayerMouseEvent) => {
        const map = this.map;
        if (!map) {
            return;
        }
        const feature = e.features?.[0];
        if (feature?.id === this.hoveredId) {
            return;
        }

        this.hoveredId = feature?.id;
        map.setFilter(RVR_HOVER_LAYER_ID, ['==', ['id'], this.hoveredId ?? -1]);
        map.getCanvas().style.cursor = 'pointer';
    };

    private handleMouseLeave = () => {
        const map = this.map;
        if (!map) {
            return;
        }
        this.hoveredId = undefined;
        map.setFilter(RVR_HOVER_LAYER_ID, ['==', ['id'], -1]);
        map.getCanvas().style.cursor = '';
    };
}

function lerpPoint(a: [number, number], b: [number, number], t: number): [number, number] {
    return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}

// Center point along the runway centerline at length fraction `t` (0 = endA, 1 = endB).
function centerPointAt(obb: RunwayOBB, t: number): [number, number] {
    const [c0, c1, c2, c3] = obb.corners;
    const rail0 = lerpPoint(c0, c1, t);
    const rail1 = lerpPoint(c3, c2, t);
    return lerpPoint(rail0, rail1, 0.5);
}

function addPoint(
    features: Feature<Point>[],
    obb: RunwayOBB,
    centerT: number,
    segment: RvrSegment,
    runwayId: string,
    value: number
): void {
    const color = getRvrColor(value);
    if (!color) {
        return;
    }

    features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: centerPointAt(obb, centerT) },
        properties: {
            segment,
            runwayId,
            value,
            label: `${runwayId} ${segment} ${formatRvrValue(value)}`,
            color,
            rotation: ((obb.bearingDeg % 90) + 90) % 90
        } as RvrFeatureProperties
    });
}

function emptyCollection(): FeatureCollection {
    return { type: 'FeatureCollection', features: [] };
}

function createRoundedSquareIcon(): ImageData {
    const size = 96;
    const half = size / 2;
    const boxHalf = half - 22;
    const cornerRadius = 5;
    const spread = 10;

    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    const imageData = ctx.createImageData(size, size);
    const data = imageData.data;

    for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
            const px = x - half + 0.5;
            const py = y - half + 0.5;
            const dist = sdRoundBox(px, py, boxHalf, cornerRadius);
            const alpha = clamp01(0.5 - dist / spread);
            const idx = (y * size + x) * 4;
            data[idx] = 255;
            data[idx + 1] = 255;
            data[idx + 2] = 255;
            data[idx + 3] = Math.round(alpha * 255);
        }
    }

    ctx.putImageData(imageData, 0, 0);
    const result = ctx.getImageData(0, 0, size, size);
    canvas.remove();
    return result;
}

function sdRoundBox(px: number, py: number, boxHalf: number, r: number): number {
    const qx = Math.abs(px) - boxHalf + r;
    const qy = Math.abs(py) - boxHalf + r;
    const outside = Math.hypot(Math.max(qx, 0), Math.max(qy, 0));
    const inside = Math.min(Math.max(qx, qy), 0);
    return outside + inside - r;
}

function clamp01(value: number): number {
    return Math.min(1, Math.max(0, value));
}
